import {inject, Injectable, makeStateKey, OnDestroy, PLATFORM_ID, TransferState} from '@angular/core';
import {Meta, Title} from '@angular/platform-browser';
import {LinkService} from './link.service';
import {Head, HeadLink, HeadMeta, Maybe} from '../interfaces/generated/graphql';
import {Subscription} from 'rxjs';
import {environment} from '@env/environment';
import {PageService} from './page.service';
import {RoutingService} from './routing.service';
import {DOCUMENT, isPlatformServer} from '@angular/common';
import {ScriptService} from '@core/services/script.service';

@Injectable({
  providedIn: 'root'
})
export class HeadService implements OnDestroy {
  private meta = inject(Meta);
  private link = inject(LinkService);
  private title = inject(Title);
  private transferState = inject(TransferState);
  private pageService = inject(PageService);
  private routingService = inject(RoutingService);
  private scriptService = inject(ScriptService);
  private platformId = inject(PLATFORM_ID);
  private document = inject<Document>(DOCUMENT);


  private tagElements: (HeadMeta | HeadLink)[] = [];
  private tagElementsStateKey = 'headTagElements';

  private pageSub: Subscription;
  public isAutoHeadersEnabled = true;

  public initHeadService() {
    if (this.pageSub) {
      return;
    }
    this.tagElements = this.transferState.get(makeStateKey(this.tagElementsStateKey), []);
    this.pageSub = this.pageService.getPageAsync().subscribe(page => {
      this.updateTitleAndTags(page?.page.title || 'Supervin', page?.page.head);
      this.updateStructuredDataScheme(page?.page.head?.structuredDataScheme);
    });
  }

  ngOnDestroy() {
    this.pageSub.unsubscribe();
  }

  findElementsByHeadTag(headTag: HeadMeta | HeadLink): HTMLElement[] {
    if ((headTag as HeadMeta).name) {
      return this.findHTMLElementsByHeadMeta(headTag as HeadMeta);
    } else {
      return this.findHTMLElementsByHeadLink(headTag as HeadLink);
    }
  }

  resetTags() {
    this.tagElements = [];
    this.transferState.set<any>(makeStateKey(this.tagElementsStateKey), this.tagElements);
  }

  findHTMLElementsByHeadLink(headLink: HeadLink): HTMLLinkElement[] {
    if (!headLink.rel || isPlatformServer(this.platformId)) {
      return [];
    }
    const escapedRel = this.escapeCssAttributeValue(headLink.rel);
    // For alternate links with hreflang, match both rel and hreflang to avoid removing all alternates
    // Note: We check the original headLink.rel value before escaping since 'alternate' is a known safe value
    if (headLink.rel === 'alternate' && headLink.hreflang) {
      const escapedHreflang = this.escapeCssAttributeValue(headLink.hreflang);
      return Array.from(document.querySelectorAll(`head link[rel="${escapedRel}"][hreflang="${escapedHreflang}"]`));
    }
    return Array.from(document.querySelectorAll(`head link[rel="${escapedRel}"]`));
  }

  findHTMLElementsByHeadMeta(headLink: HeadMeta): HTMLMetaElement[] {
    let elements: any[] = [];
    if (headLink?.name) {
      const escapedName = this.escapeCssAttributeValue(headLink.name);
      elements = Array.from(document.querySelectorAll(`head meta[name="${escapedName}"]`))
    } else if (headLink?.property) {
      const escapedProperty = this.escapeCssAttributeValue(headLink.property);
      elements = Array.from(document.querySelectorAll(`head meta[property="${escapedProperty}"]`))
    }
    return elements;
  }

  removeElementsByHeadTag(headTag: HeadMeta | HeadLink) {
    if ((headTag as HeadMeta)?.name) {
      this.meta.removeTag(`name='${(headTag as HeadMeta)?.name}'`)
    }
    if ((headTag as HeadMeta)?.property) {
      this.meta.removeTag(`property='${(headTag as HeadMeta)?.property}'`)
    }
    if ((headTag as HeadLink)?.rel) {
      const elements = this.findElementsByHeadTag(headTag);
      elements?.forEach(e => e.remove());
    }
  }

  addMetaTag(meta: HeadMeta) {
    meta = {...meta};
    this.removeElementsByHeadTag(meta);
    this.meta.addTag(this.prepareTag(meta));
    this.addTag(meta);
  }

  protected addTag(tag: HeadLink | HeadMeta) {
    this.tagElements.push(tag);

    this.transferState.set<any>(makeStateKey(this.tagElementsStateKey), this.tagElements);
  }

  addLinkTag(link: HeadLink) {
    link = {...link};
    // Only replace hostname for non-alternate links
    // Alternate links should preserve their original URLs from backend
    if (link.href && link.rel !== 'alternate') {
      link.href = this.replaceHostname(link.href);
    }
    this.removeElementsByHeadTag(link);
    this.link.addTag(this.prepareTag(link));
    this.addTag(link);
  }

  updateTitleAndTags(title: string, head: Head | null = null) {
    this.tagElements.forEach(el => this.removeElementsByHeadTag(el));
    this.resetTags();

    this.title.setTitle(title || 'Supervin');

    if (head?.meta) {
      head.meta.forEach((meta: any) => {
        this.addMetaTag(meta);
      });
    }
    if (head?.link) {
      head.link.forEach((link: any) => {
        this.addLinkTag(link);
      });
    }

    this.addRobotsTag();
  }

  protected replaceHostname(url: string): string {
    const newHostname = environment.supervin.frontendHostname;
    try {
      const urlObj = new URL(url);
      urlObj.host = newHostname;
      return urlObj.href;
    } catch (error) {
      return url;
    }
  }

  protected prepareTag(data: any) {
    const keys = Object.keys(data);
    const newObj: { [index: string]: any } = {};

    for (const key of keys) {
      if (!data[key] || key === '__typename') {
        continue;
      }

      newObj[key] = data[key];
    }

    return newObj;
  }

  /**
   * Escapes a string for safe use in CSS attribute selectors
   * to prevent CSS injection attacks.
   * Since we use double-quoted attribute selectors, we need to escape:
   * - Backslashes (must be escaped first)
   * - Double quotes
   * - Control characters (newlines, tabs, etc.)
   */
  protected escapeCssAttributeValue(value: string): string {
    if (!value) {
      return '';
    }
    return value
      .replace(/\\/g, '\\\\')  // Escape backslashes first
      .replace(/"/g, '\\"')    // Escape double quotes
      .replace(/\n/g, '\\n')   // Escape newlines
      .replace(/\r/g, '\\r')   // Escape carriage returns
      .replace(/\t/g, '\\t')   // Escape tabs
      .replace(/\f/g, '\\f')   // Escape form feed
      .replace(/\v/g, '\\v');  // Escape vertical tab
  }

  private addRobotsTag() {
    const host = this.routingService.hostname;
    if (environment.supervin.previewHostname === host) {
      this.addMetaTag({name: 'robots', content: 'noindex'});
    }
  }

  async updateStructuredDataScheme(structuredDataScheme: Maybe<string> | undefined): Promise<void> {
    if (!isPlatformServer(this.platformId)) {
      // On browser: avoid re-inserting the element to prevent a Safari bug where
      // dynamically inserted <script type="application/ld+json"> is evaluated as
      // JavaScript, causing SyntaxError: Unexpected token ','
      const existingEl = this.document.getElementById('structured-data-scheme') as HTMLScriptElement | null;
      if (!structuredDataScheme) {
        existingEl?.remove();
        return;
      }
      if (existingEl) {
        existingEl.textContent = structuredDataScheme;
        return;
      }
      // Element doesn't exist yet: insert an empty element first, then set textContent
      // so Safari sees an empty script at insertion time (no content to evaluate as JS)
      const scriptEl = this.document.createElement('script');
      scriptEl.id = 'structured-data-scheme';
      scriptEl.setAttribute('type', 'application/ld+json');
      this.document.head.appendChild(scriptEl);
      scriptEl.textContent = structuredDataScheme;
      return;
    }
    this.scriptService.removeScriptById('structured-data-scheme', 'head');
    if (!structuredDataScheme) {
      return;
    }
    await this.scriptService.addScript({
      type: 'application/ld+json',
      async: false,
      text: structuredDataScheme,
      id: 'structured-data-scheme'
    }, false, 'head', true);
  }
}
